import React, { useState, useEffect, useRef } from 'react';
import { Bell, CheckCheck, Trophy, Megaphone, Info, BookOpen, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getMyNotifications, markNotificationAsRead, markAllNotificationsAsRead } from '../services/api';
import './NotificationBell.css';

const NotificationBell = () => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const dropdownRef = useRef(null);
    const navigate = useNavigate();

    // Fetch notifications
    const fetchNotifications = async () => {
        try {
            const data = await getMyNotifications({ limit: 10 });
            setNotifications(data.data);
            setUnreadCount(data.unreadCount);
        } catch (error) {
            console.error('Failed to fetch notifications', error);
        }
    };

    // Initial fetch and periodic polling
    useEffect(() => {
        fetchNotifications();

        // Poll every 60 seconds
        const interval = setInterval(fetchNotifications, 60000);
        return () => clearInterval(interval);
    }, []);

    // Handle outside click to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const handleBellClick = () => {
        setIsOpen(!isOpen);
        if (!isOpen) {
            // Refresh on open
            fetchNotifications();
        }
    };

    const handleNotificationClick = async (notif) => {
        try {
            if (!notif.isRead) {
                await markNotificationAsRead(notif._id);
                // Update local state to reflect read status
                setNotifications(prev => prev.map(n =>
                    n._id === notif._id ? { ...n, isRead: true } : n
                ));
                setUnreadCount(prev => Math.max(0, prev - 1));
            }

            // Navigate if link exists
            if (notif.link) {
                if (notif.link.startsWith('http')) {
                    window.open(notif.link, '_blank');
                } else {
                    navigate(notif.link);
                    setIsOpen(false);
                }
            }
        } catch (error) {
            console.error('Error handling notification click', error);
        }
    };

    const handleMarkAllRead = async () => {
        try {
            setLoading(true);
            await markAllNotificationsAsRead();
            setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
            setUnreadCount(0);
        } catch (error) {
            console.error('Error marking all as read', error);
        } finally {
            setLoading(false);
        }
    };

    const getIcon = (type) => {
        switch (type) {
            case 'quiz': return <BookOpen size={18} />;
            case 'lesson': return <BookOpen size={18} />; // Replace with Video/Play icon if available in your version
            case 'achievement': return <Trophy size={18} />;
            case 'announcement': return <Megaphone size={18} />;
            case 'admin': return <AlertCircle size={18} />;
            default: return <Info size={18} />;
        }
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInSeconds = Math.floor((now - date) / 1000);

        if (diffInSeconds < 60) return 'Just now';
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
        if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
        return date.toLocaleDateString();
    };

    return (
        <div className="notification-container" ref={dropdownRef}>
            <button className="notification-bell-btn" onClick={handleBellClick}>
                <Bell size={24} />
                {unreadCount > 0 && <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>}
            </button>

            {isOpen && (
                <div className="notification-dropdown">
                    <div className="notification-header">
                        <h3>Notifications</h3>
                        {unreadCount > 0 && (
                            <button className="mark-all-read" onClick={handleMarkAllRead} disabled={loading}>
                                <CheckCheck size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                                Mark all as read
                            </button>
                        )}
                    </div>

                    <ul className="notification-list">
                        {notifications.length === 0 ? (
                            <li className="notification-empty">
                                <Bell size={48} style={{ opacity: 0.1, margin: '0 auto 10px', display: 'block' }} />
                                <p>No notifications yet</p>
                            </li>
                        ) : (
                            notifications.map(notif => (
                                <li
                                    key={notif._id}
                                    className={`notification-item ${!notif.isRead ? 'unread' : ''} type-${notif.type}`}
                                    onClick={() => handleNotificationClick(notif)}
                                >
                                    <div className="notif-icon-box">
                                        {getIcon(notif.type)}
                                    </div>
                                    <div className="notif-content">
                                        <div className="notif-title">{notif.title}</div>
                                        <div className="notif-message">{notif.message}</div>
                                        <div className="notif-time">{formatTime(notif.createdAt)}</div>
                                    </div>
                                    {!notif.isRead && (
                                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#2563eb', flexShrink: 0, marginTop: '6px' }} />
                                    )}
                                </li>
                            ))
                        )}
                    </ul>

                    {notifications.length > 0 && (
                        <div className="notification-footer">
                            <span className="view-all-btn">View all notifications</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
